import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { DisplacementResultsModel } from '../../models/displacement-results.model';
import { SelectItem } from 'primeng/api';
import { StressResultsService } from '../../services/stress-results.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { lastValueFrom } from 'rxjs';
import { SingleLoadResultsUi } from '../../models/perivis-results-state.model';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { Store } from '@ngneat/elf';
import { StressOptionsService } from '../../services/stress-options.service';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-buckling-summary',
  templateUrl: './buckling-summary.component.html',
  styles: [],
  standalone: false
})
export class BucklingSummaryComponent implements OnInit, OnDestroy {
  public displacementResults: DisplacementResultsModel[];
  public xAxisTitle = '';
  public plotName: string;
  public isLoading = signal<boolean>(true);
  public selectedResultDisplay: string = 'plot';
  public errorMsg: string;
  public results: any[];
  public columnDefs: { header: string, field: string }[];
  public columnDefinitions: any[];
  public selectedLoadCase: any;
  public depthUnit: string;
  public forceUnit: string;
  public loadCases: SelectItem[] = [];
  public signalRChange: boolean;
  public bucklingSummaryResultsStore: Store;
  public totalBuckledLength: number;
  public longLengthUnit: string;
  
  private _helicalBucklingFactor: number;
  private selectedString: string;

  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];

  public depthView = [
    { label: 'TVD', value: 'tvd' },
    { label: 'MD', value: 'md' }
  ];

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.bucklingSummaryResultsStore = this._storeService.createStore(this.componentId, new SingleLoadResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedResultDisplay = this.bucklingSummaryResultsStore.state.selectedResultDisplay || 'plot';
  }
  get componentId(): string {
    return this._componentId;
  }

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService,
    private _stressOptionsService: StressOptionsService
  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    const units = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.depthUnit = units.longLengths;
    this.forceUnit = units.force;
    this.longLengthUnit = units.longLengths;
    this.xAxisTitle = 'Effective Force (' + this.forceUnit + ')';

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    // this.refreshResults(true);


    this.columnDefs = [
      { header: 'Effective Force (' + this.forceUnit + ')', field: 'effectiveForce' },
      { header: 'Sinusoidal Buckling Load (' + this.forceUnit + ')', field: 'criticalSinusoidalBucklingForce' },
      { header: 'Helical Buckling Load (' + this.forceUnit + ')', field: 'helicalBucklingLoad' }
    ];

    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      { header: 'Effective Force (' + this.forceUnit + ')', field: 'effectiveForce', valueFormatter: (params) => this.formatDecimal(params, 100) },
      { header: 'Sinusoidal Buckling Load (' + this.forceUnit + ')', field: 'criticalSinusoidalBucklingForce', valueFormatter: (params) => this.formatDecimal(params, 100) },
      { header: 'Helical Buckling Load (' + this.forceUnit + ')', field: 'helicalBucklingLoad', valueFormatter: (params) => this.formatDecimal(params, 100) },
      { header: 'Buckling Type', field: 'bucklingType', valueFormatter: (params) => params }
    ]

    this.refreshResults(false);
    this.plotName = this.setPlotName();
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.signalRChange = true;
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true);
    }
  }

  onSelectedResultDisplayUpdated(e): void {
    if (e.value === 'plot' || e.value === 'grid') {
      this.selectedResultDisplay = e.value;
      this.bucklingSummaryResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
    }
    if (this.bucklingSummaryResultsStore.state.selectedLoadCase?.label) {
      this.plotName = this.setPlotName();
    } else {
      this.bucklingSummaryResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
    }
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  public async refreshResults(calledFromSignalR: boolean): Promise<void> {
    this.isLoading.set(true);
    this.results = [];

    if (!this.displacementResults || calledFromSignalR) {
      try {
        const [displacementResults, stressOptions] = await Promise.all([
          lastValueFrom(this._stressResultsService.getDisplacementResults()),
          lastValueFrom(this._stressOptionsService.getStressOptions())
        ]);
        this.displacementResults = displacementResults;
        this._helicalBucklingFactor = stressOptions.helicalBucklingFactor;
        this.loadCases = this.displacementResults?.map(lc => {
          return { label: lc.loadCaseName, value: lc };
        }) ?? [];
        if (!this.bucklingSummaryResultsStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.bucklingSummaryResultsStore.state.selectedLoadCase?.value.tubularStringId)) {
          this.bucklingSummaryResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
        }
      } catch {
        this.errorMsg = "Well Configuration Missing: Please specify at least one string";
        this.isLoading.set(false);
        return;
      }
    }

    if (this.displacementResults.length === 0 || this.displacementResults[0].loadCaseName === '') {
      this.errorMsg = "Please calculate PERIVIS to see results";
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    this.setLoadCase(this.bucklingSummaryResultsStore.state.selectedLoadCase ?? this.loadCases[0]);

    this.isLoading.set(false);
  }

  setLoadCase(loadCase): void {
    this.results = [];
    this.totalBuckledLength = 0;

    const displacement = this.displacementResults.find(
      x => x['loadCaseName'] === (loadCase.value.label ?? loadCase.value.loadCaseName)
    )?.displacementResults ?? [];

    let totalBuckledLength = 0;
    let previousDepth = 0;

    displacement.forEach((result, i) => {
      const critical = result.criticalSinusoidallBucklingForce;
      const effective = result.effectiveForce;
      const helicalBucklingLoad = critical * this._helicalBucklingFactor;

      let bucklingType = 'None';
      if (effective < 0 && (effective < critical || effective < helicalBucklingLoad)) {
        bucklingType = effective < helicalBucklingLoad ? 'Helical' : 'Sinusoidal';
      }

      const isCurrentlyBuckled = bucklingType !== 'None';
      if (i > 0 && isCurrentlyBuckled) {
        const segmentLength = Math.abs(result.measuredDepth - previousDepth);
        totalBuckledLength += segmentLength;
      }

      previousDepth = result.measuredDepth;

      this.results.push({
        measuredDepth: result.measuredDepth,
        trueVerticalDepth: result.trueVerticalDepth,
        effectiveForce: effective,
        criticalSinusoidalBucklingForce: critical,
        helicalBucklingLoad: helicalBucklingLoad,
        bucklingType: bucklingType
      });
    });

    this.totalBuckledLength = totalBuckledLength;

    this.bucklingSummaryResultsStore.update(state => ({ ...state, selectedLoadCase: loadCase }));
    this.plotName = this.setPlotName();
  }


  setPlotName(): string {
    const loadCase = this.bucklingSummaryResultsStore.state.selectedLoadCase;
    if (!loadCase) {
      return 'bucklingSummaryPlot - ' + this.selectedString;
    }
    return 'bucklingSummaryPlot - ' + this.selectedString + ' ' + this.bucklingSummaryResultsStore.state.selectedMode + ' ' + (loadCase.value ? loadCase.value?.label : loadCase.label);
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}
